import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Star } from 'lucide-react';
import { Link } from 'react-router';
import { Badge, Button, Card, Empty, ErrorBanner, FilterSelect, PageHeader, Pagination, Spinner } from '../../components/ui';
import { api } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { useAction, useUrlState, type Paged } from '../../lib/hooks';

interface Review { id: string; rating: number; title: string | null; body: string | null; status: string; isVerifiedPurchase: boolean; updatedAt: string; product: { id: string; name: string }; user: { id: string; customerProfile: { fullName: string } | null } }

export function Stars({ n }: { n: number }) {
  return <span className="inline-flex" aria-label={`${n} out of 5 stars`}>{[1, 2, 3, 4, 5].map((i) => <Star key={i} aria-hidden className={`h-4 w-4 ${i <= n ? 'fill-gold-500 text-gold-600' : 'text-cream-border'}`} />)}</span>;
}

export function Reviews() {
  const [f, setF] = useUrlState({ status: 'DRAFT', page: '1' });
  const q = useQuery({ queryKey: ['reviews', f], queryFn: () => api<Paged<Review>>('/admin/reviews', { query: { ...f, pageSize: 20 } }), placeholderData: keepPreviousData });
  const moderate = useAction((v: { id: string; status: 'ACTIVE' | 'ARCHIVED' }) => api(`/admin/reviews/${v.id}/moderate`, { method: 'POST', body: { status: v.status } }), { success: (r) => ((r as { status: string }).status === 'ACTIVE' ? 'Review published' : 'Review hidden'), invalidate: [['reviews']] });
  return (
    <>
      <PageHeader title="Reviews" subtitle="Only customers who received the product can review it. Reviews appear on the website after approval." actions={<FilterSelect label="Show" value={f.status} onChange={(v) => setF({ status: v })} options={[{ value: 'DRAFT', label: 'Waiting for approval' }, { value: 'ACTIVE', label: 'Published' }, { value: 'ARCHIVED', label: 'Hidden' }]} />} />
      {q.isLoading ? <Spinner /> : q.error ? <ErrorBanner error={q.error} /> : (
        <div className="space-y-3">
          {!q.data!.items.length && <Card><Empty>{f.status === 'DRAFT' ? 'No reviews waiting. 🎉' : 'None.'}</Empty></Card>}
          {q.data!.items.map((r) => (
            <Card key={r.id}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2"><Stars n={r.rating} />{r.isVerifiedPurchase && <Badge tone="green">Verified purchase</Badge>}<Badge status={r.status} /></div>
                  {r.title && <p className="mt-1 font-semibold">{r.title}</p>}
                  {r.body && <p className="mt-1 whitespace-pre-wrap text-sm">{r.body}</p>}
                  <p className="mt-2 text-xs text-ink-muted">{r.user.customerProfile?.fullName ?? 'Customer'} on <Link className="underline" to={`/products/${r.product.id}`}>{r.product.name}</Link> · {dateTime(r.updatedAt)}</p>
                </div>
                <div className="flex gap-2">
                  {r.status !== 'ACTIVE' && <Button onClick={() => moderate.mutate({ id: r.id, status: 'ACTIVE' })}>Publish</Button>}
                  {r.status !== 'ARCHIVED' && <Button variant="secondary" onClick={() => moderate.mutate({ id: r.id, status: 'ARCHIVED' })}>Hide</Button>}
                </div>
              </div>
            </Card>
          ))}
          <Pagination page={q.data!.page} totalPages={q.data!.totalPages} total={q.data!.total} onPage={(p) => setF({ page: String(p) } as never)} />
        </div>
      )}
    </>
  );
}
